import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Briefcase,
  Clock,
  CreditCard,
  Globe,
  Image as ImageIcon,
  Phone,
  Route,
  Search,
  SlidersHorizontal,
  Store,
  Timer,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';

interface Tab {
  id: string;
  path: string;
  label: string;
  icon: React.ElementType;
}

const tabs: Tab[] = [
  { id: 'basic',    path: '/settings/basic',            label: 'restaurants.nav.basicInfo',     icon: Store      },
  { id: 'media',    path: '/settings/media',            label: 'restaurants.nav.media',         icon: ImageIcon  },
  { id: 'contact',  path: '/settings/contact',          label: 'restaurants.nav.contact',       icon: Phone      },
  { id: 'business', path: '/settings/business',         label: 'restaurants.nav.business',      icon: Briefcase  },
  { id: 'hours',    path: '/settings/hours',            label: 'restaurants.nav.hours',         icon: Clock      },
  { id: 'zones',    path: '/settings/zones',            label: 'restaurants.nav.zones',         icon: Route      },
  { id: 'estimates', path: '/settings/estimates',         label: 'restaurants.nav.estimates',     icon: Timer      },
  { id: 'payments',  path: '/settings/payment-settings',  label: 'restaurants.nav.payments',      icon: CreditCard },
  { id: 'notifications', path: '/settings/notifications', label: 'restaurants.nav.notifications', icon: Bell   },
  { id: 'domain',   path: '/settings/domain',            label: 'restaurants.nav.domain',        icon: Globe      },
  { id: 'seo',      path: '/settings/seo',              label: 'restaurants.nav.seo',           icon: Search     },
];

export function SettingsLayout() {
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const base = `/app/restaurants/${restaurantId}`;

  const navRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    const update = () => {
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm shrink-0">
        <div className="px-4 lg:px-6 pt-5 pb-0">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 shrink-0">
              <SlidersHorizontal className="h-4 w-4 text-accent" />
            </div>
            <h1 className="text-lg font-semibold tracking-tight">
              {t('restaurants.nav.settings')}
            </h1>
          </div>

          {/* Tab strip — scroll-fades tell mobile users there are more tabs */}
          <div className="relative -mx-4 lg:-mx-6">
            {/* Left fade */}
            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 transition-opacity duration-150',
                'bg-gradient-to-r from-card/90 to-transparent',
                canScrollLeft ? 'opacity-100' : 'opacity-0',
              )}
            />
            {/* Right fade */}
            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 transition-opacity duration-150',
                'bg-gradient-to-l from-card/90 to-transparent',
                canScrollRight ? 'opacity-100' : 'opacity-0',
              )}
            />

            <nav
              ref={navRef}
              aria-label={t('restaurants.nav.settings')}
              className="overflow-x-auto scrollbar-none px-4 lg:px-6"
            >
              <div className="flex items-center gap-0.5 min-w-max pb-px">
                {tabs.map((tab) => {
                  const to = `${base}${tab.path}`;
                  return (
                    <NavLink
                      key={tab.id}
                      to={to}
                      className={({ isActive }) =>
                        cn(
                          'group flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap',
                          'border-b-2 -mb-px transition-all duration-150',
                          isActive
                            ? 'border-accent text-accent bg-accent/5'
                            : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <tab.icon
                            className={cn(
                              'h-3.5 w-3.5 shrink-0 transition-colors',
                              isActive ? 'text-accent' : 'text-muted-foreground group-hover:text-foreground',
                            )}
                          />
                          <span>{t(tab.label)}</span>
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* Page content */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-muted/20">
        <Outlet />
      </div>
    </div>
  );
}
